import { ArrowRight } from "lucide-react";

/**
 * Transition pages shown between quiz sections.
 * Keyed by the question index *just answered* (0-based, current array):
 *   6  -> after Q9  -> Foundation Builders
 *   9  -> after Q12 -> Quick Connect
 *   14 -> after Q15 (last Sensory Sync) -> Sensory Sync
 *   18 -> after Q19 -> Grand Vision (then -> /lead-capture)
 */
export const TRANSITIONS = {
  6: {
    id: "foundation-builders",
    kicker: "FOUNDATION BUILDERS",
    header: "Most couples never actually say what they Really Feel",
    body: [
      "One of the biggest things we hear from couples is:",
      "\u201CWe talk all the time\u2026 but either one of us doesn\u2019t seem to listen.\u201D",
      "Research consistently shows that emotional disconnection and \u201Cgrowing apart\u201D are among the leading reasons couples separate, often more than a single dramatic event like infidelity or finances.",
      "It is usually not one explosion. It is a drift.",
      "Real connection happens when people feel safe enough to be honest, vulnerable, and emotionally seen, not just when logistics are handled well. Listening is what creates that space.",
      "The couples who create lasting connection are usually the ones willing to have the deeper conversations most people avoid.",
    ],
    testimonial: {
      name: "Richard",
      body: "The Connexus cards challenged us in the best way and deepened our connection and commitment.",
    },
    footer: "Let\u2019s look at how your daily connection is really functioning.",
    cta: "Continue",
  },
  9: {
    id: "quick-connect",
    kicker: "QUICK CONNECT",
    header: "Relationships don\u2019t usually break in one Big Moment",
    body: [
      "Most couples slowly disconnect through the little things:",
      { list: ["Less check-ins.", "More phones.", "More logistics.", "Less presence."] },
      "Research actually shows that small daily moments of connection have a massive impact on long-term relationship happiness and emotional closeness.",
      "Couples who stay emotionally engaged throughout daily life tend to feel more supported, connected, and secure together.",
    ],
    testimonial: {
      name: "Jennifer",
      body: "These practices brought us closer during one of our most disconnected seasons.",
    },
    footer: "Now let\u2019s explore intimacy and physical connection.",
    cta: "Continue",
  },
  14: {
    id: "sensory-sync",
    kicker: "SENSORY SYNC",
    header: "Intimacy changes everything in a Relationship",
    body: [
      "We\u2019ve seen couples go from feeling like roommates\u2026 to laughing, touching, flirting, and actually wanting each other again.",
      "Not because they suddenly became \u201Cbetter\u201D people.",
      "But because they started intentionally reconnecting emotionally and physically again.",
      "Research consistently shows that emotional intimacy and physical intimacy are deeply connected to relationship satisfaction and long-term fulfillment.",
    ],
    testimonial: {
      name: "Jennifer",
      body: "That card about loving our bodies, really got us going in the bedroom.",
    },
    footer: "Finally, let\u2019s look at the future you are building together.",
    cta: "Continue",
  },
  18: {
    id: "grand-vision",
    kicker: "GRAND VISION",
    header: "Couples who build together stay Connected Longer",
    body: [
      "One of the fastest ways couples drift apart is when they stop dreaming together.",
      "The relationship slowly becomes:",
      { list: ["work", "responsibilities", "routines", "survival"] },
      "The happiest couples often feel like they are moving toward something together, not just managing life side-by-side.",
      "Shared goals, shared experiences, and shared vision create emotional alignment and a stronger sense of partnership over time.",
    ],
    testimonial: {
      name: "Cristina",
      body: "A more fun and effective way to grow my relationship than reading books.",
    },
    footer: "Your results are ready.",
    cta: "See My Results",
  },
};

export default function QuizTransition({ data, onContinue }) {
  return (
    <div
      className="min-h-screen bg-connexus-surface flex items-start justify-center px-5 py-10 sm:py-16"
      data-testid={`quiz-transition-${data.id}`}
    >
      <div className="w-full max-w-xl flex flex-col items-stretch fade-up">
        <p
          className="text-[11px] sm:text-xs tracking-[0.3em] font-bold text-connexus mb-6 text-left"
          data-testid="transition-kicker"
        >
          {data.kicker}
        </p>

        <h1
          className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-connexus mb-8 text-left"
          data-testid="transition-header"
        >
          {data.header}
        </h1>

        <div className="flex flex-col gap-5 text-[15px] sm:text-base leading-[1.7] text-connexus-textSecondary mb-10 text-left w-full max-w-[36ch] sm:max-w-prose">
          {data.body.map((block, idx) => {
            if (typeof block === "string") {
              return <p key={idx}>{block}</p>;
            }
            if (block.list) {
              return (
                <ul
                  key={idx}
                  className="flex flex-col gap-2 list-none text-connexus-textSecondary"
                >
                  {block.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              );
            }
            return null;
          })}
        </div>

        {/* Testimonial — product-anchored card */}
        <figure
          className="w-full max-w-[36ch] sm:max-w-prose bg-connexus-accent/60 border border-connexus-border rounded-2xl px-5 py-5 mb-10 text-left"
          data-testid="transition-testimonial"
        >
          <div className="flex items-center gap-3 mb-3">
            <img
              src="https://customer-assets.emergentagent.com/job_cross-platform-app-100/artifacts/uxv7q2f0_Main%20Product%20Connexus.jpg"
              alt="Connexus Couples Card Deck"
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-contain bg-white border border-connexus-border shrink-0"
              data-testid="transition-product-thumb"
            />
            <p
              className="text-[12px] sm:text-[13px] font-semibold tracking-wide text-connexus leading-snug"
              data-testid="transition-product-name"
            >
              Connexus Couples Card Deck
            </p>
          </div>
          <blockquote className="text-[15px] italic leading-[1.7] text-connexus-textSecondary">
            &ldquo;{data.testimonial.body}&rdquo;
          </blockquote>
          <figcaption className="mt-2 text-[12px] tracking-wider font-semibold text-connexus">
            — {data.testimonial.name}
          </figcaption>
        </figure>

        <p
          className="text-[15px] sm:text-base leading-[1.7] text-connexus-textSecondary mb-7 w-full max-w-[36ch] sm:max-w-prose text-left"
          data-testid="transition-footer"
        >
          {data.footer}
        </p>

        <div className="flex justify-center">
          <button
            onClick={onContinue}
            className="inline-flex items-center justify-center gap-3 bg-connexus hover:bg-connexus-hover text-white px-8 py-4 rounded-full text-base font-semibold shadow-connexus-soft transition-all active:scale-[0.98]"
            data-testid="transition-continue-button"
          >
            {data.cta}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
